import {
  Add as AddIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  Chip,
  Container,
  Divider,
  IconButton,
  Modal,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { colors } from '../../constants/constants';
import { selectFrameworkControlActivityList } from '../../redux/reducers/activityReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import {
  ApplicabilityReasons,
  ImplementationStatus,
  InapplicabilityReasons,
  updateGroups,
} from '../../redux/reducers/frameworkReducer';
import DocumentLink from '../DocumentLink';
import Activity from './Activity';
import OwnerSelector from './OwnerSelector';

function SOAEditControl({ control, handleClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [newControl, setNewControl] = useState(control);
  const [showActivities, setShowActivities] = useState(false);
  const activities = useSelector((state) => selectFrameworkControlActivityList(state, control.id));

  useEffect(() => {
    setNewControl(control);
  }, [control]);

  const memoTextChange = useCallback((event) => {
    const { name, value } = event.target;
    const newDetails = {
      ...newControl,
      [name]: value,
    };
    setNewControl(newDetails);
  }, [newControl]);

  const handleTextChange = useMemo(
    () => debounce(memoTextChange, 150),
    [memoTextChange],
  );

  const toggleApplicability = () => {
    const newDetails = {
      ...newControl,
      applicable: !newControl.applicable,
      applicabilityReasons: [],
    };
    setNewControl(newDetails);
  };

  const handleReasonChange = (value) => {
    const reasons = newControl.applicabilityReasons.includes(value)
      ? newControl.applicabilityReasons.filter((reason) => reason !== value)
      : [...newControl.applicabilityReasons, value];
    const newDetails = {
      ...newControl,
      applicabilityReasons: reasons,
    };
    setNewControl(newDetails);
  };

  const getChipLabel = (reason) => {
    let label;
    if (newControl.applicable) {
      label = `${t(`control.evaluation.applicability.${reason}Desc`)} (${t(`control.evaluation.applicability.${reason}`)})`;
    } else {
      label = `${t(`control.evaluation.inApplicability.${reason}Desc`)}`;
    }
    return label;
  };

  const handleAddLink = () => {
    const newLink = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    const newDetails = {
      ...newControl,
      links: [...newControl.links, newLink],
    };
    setNewControl(newDetails);
  };

  const handleUpdateDocument = (documentData) => {
    const newDetails = {
      ...newControl,
      links: newControl.links.map((link) => (link.id === documentData.id ? documentData : link)),
    };
    setNewControl(newDetails);
  };

  const handleDeleteDocument = (id) => {
    const newDetails = {
      ...newControl,
      links: newControl.links.filter((link) => (link.id !== id)),
    };
    setNewControl(newDetails);
  };

  function handlePersonChange(value) {
    const newDetails = {
      ...newControl,
      owner: value,
    };
    setNewControl(newDetails);
  }

  const handleButtonGroupSelect = (name, value) => {
    const newDetails = { ...newControl };
    newDetails[name] = value;
    setNewControl(newDetails);
  };

  const title = useSelector((state) => selectTitle(state, control.id));

  const handleCancel = () => {
    handleClose();
  };
  const handleSave = () => {
    dispatch(updateGroups(newControl));
    handleClose();
  };
  const toggleShowActivities = () => {
    setShowActivities(!showActivities);
  };

  return (
    <Modal open>
      <Container variant="modal" disableGutters>
        <Stack variant="modalTop" spacing={3}>
          <Typography variant="h6">{`${t('control.editControl')}: ${title}`}</Typography>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>{t('control.isControlApplicable')}</Typography>
              <Switch checked={newControl.applicable} onChange={toggleApplicability} />
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {newControl.applicable ? (
                ApplicabilityReasons.list().map((reason) => (
                  <Chip
                    key={reason}
                    size="medium"
                    label={getChipLabel(reason)}
                    color={newControl.applicabilityReasons.includes(reason) ? 'primary' : 'default'}
                    onClick={() => handleReasonChange(reason)}
                  />
                ))
              ) : (
                InapplicabilityReasons.list().map((reason) => (
                  <Chip
                    key={reason}
                    size="medium"
                    label={getChipLabel(reason)}
                    color={newControl.applicabilityReasons.includes(reason) ? 'primary' : 'default'}
                    onClick={() => handleReasonChange(reason)}
                  />
                ))
              )}
            </Stack>
            <TextField
              key={newControl.applicable ? 'description' : 'exclusionJustification'}
              size="small"
              name={newControl.applicable ? 'description' : 'exclusionJustification'}
              defaultValue={newControl.applicable
                ? newControl.description
                : newControl.exclusionJustification}
              onChange={handleTextChange}
              multiline
              rows="4"
              InputLabelProps={{ shrink: true }}
              label={newControl.applicable
                ? t('control.applicabilityJustification')
                : t('control.exclusionJustification')}
            />
          </Stack>
          <Divider flexItem sx={{ backgroundColor: colors.dirtyWhite }} />
          <Typography>{t('control.controlDetails')}</Typography>
          {newControl.applicable && (
            <Stack width="50%">
              <OwnerSelector
                id={control.id}
                handlePersonChange={handlePersonChange}
                owner={newControl.owner}
                editMode
                hideAddOwner
              />
            </Stack>
          )}
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{t('control.evaluation.evidenceLink')}</Typography>
              <Button size="small" variant="outlined" onClick={handleAddLink} endIcon={<AddIcon />}>
                {t('control.evaluation.evidenceAdd')}
              </Button>
            </Stack>
            {newControl.links.map((link) => (
              <DocumentLink
                key={link.documentName}
                document={link}
                handleDeleteDocument={handleDeleteDocument}
                handleUpdateDocument={handleUpdateDocument}
              />
            ))}
          </Stack>
          {newControl.applicable && (
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography>{t('control.status')}</Typography>
                <ButtonGroup
                  fullWidth
                  variant="outlined"
                  subtype="buttonGroupSelect"
                  disableElevation
                  color="secondary"
                  sx={{ height: '30px' }}
                >
                  {ImplementationStatus.list().map((status) => (
                    <Button
                      variant={newControl.implementationStatus === status ? 'contained' : 'outlined'}
                      subtype="buttonGroupSelect"
                      key={status}
                      disabled={false}
                      color="secondary"
                      onClick={() => handleButtonGroupSelect('implementationStatus', status)}
                      sx={{ minWidth: 'fit-content' }}
                    >
                      {t(`control.statusType.${status}`)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
              <Stack spacing={1} sx={{ p: '1px' }}>
                <Stack variant="frameworkButtonRow" direction="row">
                  {newControl.activities.length === 0 ? (
                    <Stack>
                      <Typography>{t('control.relatedActivities')}</Typography>
                      <Typography variant="body2">{t('control.noRelatedActivities')}</Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Typography>
                        {`${t('control.relatedActivities')} (${newControl.activities.length})`}
                      </Typography>
                      <IconButton onClick={toggleShowActivities}>{showActivities ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                    </Stack>
                  )}

                </Stack>
                {showActivities && activities.map((activity) => (
                  <Activity key={activity.id} activity={activity} />
                ))}
              </Stack>
            </Stack>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
              {t('control.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => handleSave()}
            >
              {t('control.saveChanges')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Modal>
  );
}

export default SOAEditControl;
